/* __next_internal_client_entry_do_not_use__ Product auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { RetailerFooter } from "@faire/retailer-visitor-shared/components/Footer/RetailerFooter";
import { AddedToBagContainer } from "@faire/retailer-visitor-shared/components/Notifications/AddedToBag/AddedToBagStore";
import { useAddedToBag } from "@faire/retailer-visitor-shared/components/Notifications/AddedToBag/useAddedToBag";
import { AddToCart } from "@faire/retailer-visitor-shared/events";
import { QuickAddModalContext, QuickAddModalEvent } from "@faire/retailer-visitor-shared/features/ProductQuickAddModal/QuickAddModalContext";
import { useFaireDirectSignUp } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectSignUp/useFaireDirectSignUp";
import { isOnlineOnlyRestricted } from "@faire/retailer-visitor-shared/lib/brandRestrictionUtils";
import { canAddToCart } from "@faire/retailer-visitor-shared/lib/canAddToCart";
import { useIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { requestIdleCallbackOrFallback } from "@faire/retailer-visitor-shared/lib/requestIdleCallbackOrFallback";
import { BrandOrderReviewPageNavigationSourceContext } from "@faire/retailer-visitor-shared/lib/useBrandOrderReviewPageNavigationSourceContext";
import { trackEditCartItemEvent, trackProductClick } from "@faire/retailer-visitor-shared/services/Events";
import { RetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { useBrandCart } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useBrandCart";
import { useCartBrand } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useCartBrand";
import { useNumberOfItemsInCart } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useNumberOfItemsInCart";
import { BrandCartEvents } from "@faire/retailer-visitor-shared/views/brand/BrandCart/index";
import { LazyBrandCart } from "@faire/retailer-visitor-shared/views/brand/BrandCart/lazyBrandCart";
import { usePreloadBuyItAgainAndImages } from "@faire/retailer-visitor-shared/views/brand/hooks/useFetchCachedBrandView";
import { preloadBrandPageLoggedIn } from "@faire/retailer-visitor-shared/views/brand/preloadBrandPageLoggedIn";
import { brandPageQueryObjToBrandPageQueryParams } from "@faire/retailer-visitor-shared/views/brand/utils/brandPageQueryObjToBrandPageQueryParams";
import { shouldShowRscLibp } from "@faire/retailer-visitor-shared/views/brand/utils/shouldShowRscLibp";
import { EmployeeProductActions } from "@faire/retailer-visitor-shared/views/product/__internal__/Product/EmployeeProductActions";
import { ProductModals } from "@faire/retailer-visitor-shared/views/product/__internal__/Product/ProductModals";
import { RelatedSearchList } from "@faire/retailer-visitor-shared/views/product/__internal__/Product/RelatedSearchList";
import { ReviewsSection } from "@faire/retailer-visitor-shared/views/product/__internal__/Product/ReviewsSection";
import { ProductHeader } from "@faire/retailer-visitor-shared/views/product/__internal__/ProductHeader/ProductHeader";
import { ProductStickyBrandHeader } from "@faire/retailer-visitor-shared/views/product/__internal__/ProductHeader/ProductStickyBrandHeader";
import { ProductLoadingSkeleton } from "@faire/retailer-visitor-shared/views/product/__internal__/ProductLoadingSkeleton";
import { BrandInfoRowFallback, LazyBrandInfoRow } from "@faire/retailer-visitor-shared/views/product/BrandInfoRow/index";
import { LazyBrandValuesSection } from "@faire/retailer-visitor-shared/views/product/BrandValuesSection/index";
import { AnimatedFaireDirectSignUpBanner } from "@faire/retailer-visitor-shared/views/product/Components/AnimatedFaireDirectSignUpBanner";
import { SEOProductInfo } from "@faire/retailer-visitor-shared/views/product/Components/SEOProductInfo";
import { ProductContent } from "@faire/retailer-visitor-shared/views/product/Content";
import { LazyNotFoundPlaceholder } from "@faire/retailer-visitor-shared/views/product/NotFoundPlaceholder";
import { LazyProductLayoutElement } from "@faire/retailer-visitor-shared/views/product/ProductLayoutElements/index";
import { ProductPageEffects } from "@faire/retailer-visitor-shared/views/product/ProductPageEffects";
import { useProductState } from "@faire/retailer-visitor-shared/views/product/useProductState";
import { generateBrandPageQueryObjForProductPage } from "@faire/retailer-visitor-shared/views/product/utils/generateBrandUrlForProductPage";
import { getProductDescription } from "@faire/retailer-visitor-shared/views/product/utils/productUtils";
import { ReviewFormSource } from "@faire/web--source/common/consts/REVIEW_FORM_SOURCES";
import { TelemetryManager } from "@faire/web--source/common/opentelemetry/TelemetryManager";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { parseQueryParams } from "@faire/web--source/common/query-params/parseQueryParams";
import { generateTracingMetadata } from "@faire/web--source/common/tracing/generateTracingMetadata";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { Row } from "@faire/web--source/slate/Layout/Row";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { trackCartProductPageBrandCartViewBagClick } from "@faire/web-api--source/events/cart/click/productPageBrandCartViewBag";
import { trackPricingProductPageBrandCartCheckoutClick } from "@faire/web-api--source/events/pricing/click/productPageBrandCartCheckout";
import { trackProductPageSidecartToggleClick } from "@faire/web-api--source/events/productPage/click/sidecartToggle";
import { IProductPageLayoutRequest } from "@faire/web-api--source/indigofair/data/IProductPageLayoutRequest";
import brandbrandToken from "@faire/web-api--source/routes/brand/brandToken";
import flashSale from "@faire/web-api--source/routes/flash-sale";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react";
import React, { Suspense, useCallback, useEffect } from "react";
import { Redirect } from "react-router-dom";
import styled from "styled-components";
var PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
var CAROUSEL_LOADING_OFFSET = -300;
var ProductBase = function(param) {
    var productToken = param.productToken;
    var isBrandPreview = useIsBrandPreview();
    var isLoggedInRetailerOrBrandPreview = useIsLoggedInRetailerOrBrandPreview();
    var name = isLoggedInRetailerOrBrandPreview ? "page-product-logged-in" : "page-product-logged-out";
    TelemetryManager.get().startTrace("uplt-".concat(name));
    var queryParams = useQueryParams(function(s) {
        return parseQueryParams(s !== null && s !== void 0 ? s : "");
    });
    var productState = useProductState(productToken);
    var brandReviewSummary = productState.brandReviewSummary, isLoading = productState.isLoading, clientProductResponseDataUpdatedAt = productState.clientProductResponseDataUpdatedAt, product = productState.product, brand = productState.brand, isBrandActive = productState.isBrandActive, badges = productState.badges, importShipmentInfo = productState.importShipmentInfo, infoTexts = productState.infoTexts, disclaimers = productState.disclaimers, productAttributeTags = productState.productAttributeTags, videos = productState.videos, showroomData = productState.showroomData, samplesBannerDetails = productState.samplesBannerDetails, productRelatedSearches = productState.productRelatedSearches, productReviewSummary = productState.productReviewSummary;
    var viewport = useViewport();
    var userToken = useUserStore([
        "userToken"
    ]).userToken;
    var loadTimingRef = useUserPerceivedLoadTimingWithKey({
        name: name,
        pageLoadKey: name,
        isPageLoading: isLoading,
        dataUpdatedAt: clientProductResponseDataUpdatedAt,
        pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
        enableTrackPerformanceBreakdown: "internal-tracking",
        trackResultCallback: function(name, status, loadType, result) {
            var metadata = generateTracingMetadata({
                name: name,
                loadType: loadType,
                status: status,
                result: result,
                userToken: userToken,
                isDesktopAndAbove: viewport.isDesktopAndAbove,
                isMobile: viewport.isMobile
            });
            TelemetryManager.get().endTrace("uplt-".concat(name), metadata);
        }
    });
    var containerRef = /*#__PURE__*/ React.createRef();
    var _brand_token;
    var cartBrand = useCartBrand((_brand_token = brand === null || brand === void 0 ? void 0 : brand.token) !== null && _brand_token !== void 0 ? _brand_token : "");
    var _brand_token1;
    var refetch = useBrandCart((_brand_token1 = brand === null || brand === void 0 ? void 0 : brand.token) !== null && _brand_token1 !== void 0 ? _brand_token1 : "").refetch;
    var _brand_token2;
    var numberOfItemsInCart = useNumberOfItemsInCart((_brand_token2 = brand === null || brand === void 0 ? void 0 : brand.token) !== null && _brand_token2 !== void 0 ? _brand_token2 : "");
    var ignoreContainer = useAddedToBag().ignoreContainer;
    var _useState = _sliced_to_array(useState(false), 2), showSideCart = _useState[0], setShowSideCart = _useState[1];
    var setShowSideCartDynamic = function() {
        if (showSideCart) {
            var _numberOfItemsInCart_toString;
            trackProductPageSidecartToggleClick("close", (_numberOfItemsInCart_toString = numberOfItemsInCart === null || numberOfItemsInCart === void 0 ? void 0 : numberOfItemsInCart.toString()) !== null && _numberOfItemsInCart_toString !== void 0 ? _numberOfItemsInCart_toString : "");
            ignoreContainer(AddedToBagContainer.BRAND_CART, false);
        } else {
            var _numberOfItemsInCart_toString1;
            trackProductPageSidecartToggleClick("open", (_numberOfItemsInCart_toString1 = numberOfItemsInCart === null || numberOfItemsInCart === void 0 ? void 0 : numberOfItemsInCart.toString()) !== null && _numberOfItemsInCart_toString1 !== void 0 ? _numberOfItemsInCart_toString1 : "");
            ignoreContainer(AddedToBagContainer.BRAND_CART, true);
        }
        setShowSideCart(!showSideCart);
    };
    var isFaireDirectSignUpEligible = useFaireDirectSignUp().isFaireDirectSignUpEligible;
    var enableSideCart = viewport.isDesktopAndAbove && canAddToCart();
    // We still have minor M&M traffic, keeping this for safety
    if (productToken.startsWith("mmp")) {
        return /*#__PURE__*/ _jsx(Redirect, {
            to: flashSale()
        });
    } else if (isLoading) {
        return /*#__PURE__*/ _jsx(ProductLoadingSkeleton, {});
    } else if (product && brand) {
        var _product_name, _product_name1;
        var _product_name_translated, _queryParams_get, _brand_token3;
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Container, {
                    children: [
                        /*#__PURE__*/ _jsx(SEOProductInfo, {
                            productName: (_product_name_translated = (_product_name = product.name) === null || _product_name === void 0 ? void 0 : _product_name.translated) !== null && _product_name_translated !== void 0 ? _product_name_translated : (_product_name1 = product.name) === null || _product_name1 === void 0 ? void 0 : _product_name1.original,
                            productDescription: getProductDescription(product),
                            brandCompanyName: brand.name
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            align: "flex-start",
                            children: [
                                /*#__PURE__*/ _jsxs(ProductWrapper, {
                                    ref: containerRef,
                                    children: [
                                        /*#__PURE__*/ _jsx(Wrapper, {
                                            children: /*#__PURE__*/ _jsx(ProductHeader, {
                                                brand: brand,
                                                product: product,
                                                hideBreadcrumbs: enableSideCart
                                            })
                                        }),
                                        enableSideCart ? /*#__PURE__*/ _jsx(ProductStickyBrandHeader, {
                                            product: product,
                                            brand: brand,
                                            containerRef: containerRef,
                                            brandReviewSummary: brandReviewSummary,
                                            showShowroomBadge: !!(showroomData === null || showroomData === void 0 ? void 0 : showroomData.isMidPricePointApparelProduct),
                                            showSidecart: showSideCart,
                                            setCart: setShowSideCartDynamic,
                                            numberOfProductsInCart: numberOfItemsInCart,
                                            cartTotal: cartBrand === null || cartBrand === void 0 ? void 0 : cartBrand.total
                                        }) : null,
                                        /*#__PURE__*/ _jsx(Wrapper, {
                                            children: /*#__PURE__*/ _jsx(ProductContent, {
                                                productToken: productToken,
                                                brand: brand,
                                                product: product,
                                                badges: badges,
                                                brandReviewSummary: brandReviewSummary,
                                                importShipmentInfo: importShipmentInfo,
                                                infoTexts: infoTexts,
                                                showroomData: showroomData,
                                                disclaimers: disclaimers,
                                                preselectedProductOptionToken: (_queryParams_get = queryParams.get("productOptionToken")) !== null && _queryParams_get !== void 0 ? _queryParams_get : undefined,
                                                productAttributeTags: productAttributeTags,
                                                productVideos: videos,
                                                samplesBannerDetails: samplesBannerDetails,
                                                loadTimingRef: loadTimingRef,
                                                showSideCart: enableSideCart && showSideCart
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(MoreFromBrand, {
                                            brand: brand,
                                            product: product
                                        }),
                                        /*#__PURE__*/ _jsx(BrandOrderReviewPageNavigationSourceContext.Provider, {
                                            value: ReviewFormSource.product_page,
                                            children: /*#__PURE__*/ _jsx(ReviewsSection, {
                                                brand: brand,
                                                product: product,
                                                productReviewSummary: productReviewSummary,
                                                brandReviewSummary: brandReviewSummary
                                            })
                                        })
                                    ]
                                }),
                                enableSideCart ? /*#__PURE__*/ _jsx(DynamicBrandCart, {
                                    brandToken: (_brand_token3 = brand.token) !== null && _brand_token3 !== void 0 ? _brand_token3 : "",
                                    showSidecart: showSideCart,
                                    "data-test-id": "brand-cart-column"
                                }) : null
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(ProductWrapper, {
                            children: [
                                !isLoggedInRetailerOrBrandPreview ? /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
                                    children: /*#__PURE__*/ _jsx(Suspense, {
                                        fallback: null,
                                        children: /*#__PURE__*/ _jsx(LazyBrandValuesSection, {
                                            productToken: productToken
                                        })
                                    })
                                }) : null,
                                /*#__PURE__*/ _jsx(QuickAddModalContext.Provider, {
                                    value: {
                                        onEvent: function(event) {
                                            if (enableSideCart && event === QuickAddModalEvent.ADD_TO_CART) {
                                                refetch();
                                            }
                                        }
                                    },
                                    children: /*#__PURE__*/ _jsx(ProductLayoutElements, {
                                        productToken: productToken
                                    })
                                }),
                                /*#__PURE__*/ _jsx(RelatedSearchList, {
                                    productRelatedSearches: productRelatedSearches
                                }),
                                /*#__PURE__*/ _jsx(ProductModals, {
                                    productToken: productToken,
                                    brand: brand,
                                    brandToken: brand === null || brand === void 0 ? void 0 : brand.token,
                                    queryParams: queryParams
                                }),
                                /*#__PURE__*/ _jsx(EmployeeProductActions, {
                                    product: product,
                                    isBrandActive: isBrandActive
                                })
                            ]
                        })
                    ]
                }),
                isLoading || isBrandPreview ? null : /*#__PURE__*/ _jsx(RetailerFooter, {}),
                isFaireDirectSignUpEligible ? /*#__PURE__*/ _jsx(AnimatedFaireDirectSignUpBanner, {}) : null,
                /*#__PURE__*/ _jsx(ProductPageEffects, {
                    brand: brand,
                    product: product,
                    queryParams: queryParams,
                    productToken: productToken
                }),
                /*#__PURE__*/ _jsx(PreloadBrandPageEffects, {
                    brandToken: brand === null || brand === void 0 ? void 0 : brand.token,
                    productToken: productToken
                })
            ]
        });
    } else {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(ProductLoadingSkeleton, {}),
            children: /*#__PURE__*/ _jsx(LazyNotFoundPlaceholder, {})
        });
    }
};
export var Product = observer(ProductBase);
Product.displayName = "ProductPage";
var Container = styled.div.withConfig({
    componentId: "sc-483ed9e9-0"
})([
    "position:relative;width:100%;margin:0 auto;",
    "{padding-bottom:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing("10x"));
var ProductWrapper = styled.div.withConfig({
    componentId: "sc-483ed9e9-1"
})([
    "max-width:1280px;margin:0 auto;width:100%;"
]);
var Wrapper = styled.div.withConfig({
    componentId: "sc-483ed9e9-2"
})([
    "",
    "{padding:",
    ";}"
], responsiveMediaQueries.tabletAndAbove, getSpacing(0, "6x"));
var PreloadBrandPageEffects = function(param) {
    var brandToken = param.brandToken, productToken = param.productToken;
    var isLoggedInRetailerOrBrandPreview = useIsLoggedInRetailerOrBrandPreview();
    var queryClient = useQueryClient();
    var _useNextRouterContext = useNextRouterContext(), nextPrefetch = _useNextRouterContext.prefetch;
    usePreloadBuyItAgainAndImages(brandToken !== null && brandToken !== void 0 ? brandToken : "");
    useEffect(function() {
        if (isLoggedInRetailerOrBrandPreview && brandToken) {
            var queryParams = generateBrandPageQueryObjForProductPage(productToken);
            requestIdleCallbackOrFallback(function() {
                if (shouldShowRscLibp()) {
                    var nextQueryParams = brandPageQueryObjToBrandPageQueryParams(queryParams);
                    nextPrefetch(brandbrandToken(brandToken, nextQueryParams));
                } else {
                    preloadBrandPageLoggedIn(brandToken, queryParams, queryClient);
                }
            });
        }
    }, [
        brandToken,
        productToken,
        queryClient,
        isLoggedInRetailerOrBrandPreview,
        nextPrefetch
    ]);
    return null;
};
var DynamicBrandCart = function(param) {
    var brandToken = param.brandToken, showSidecart = param.showSidecart;
    var refetch = useBrandCart(brandToken).refetch;
    var handleCartItemsUpdatedEvent = useCallback(function() {
        refetch();
    }, [
        refetch
    ]);
    useEffect(function() {
        AddToCart.subscribe(handleCartItemsUpdatedEvent);
        return function() {
            AddToCart.unsubscribe(handleCartItemsUpdatedEvent);
        };
    }, [
        handleCartItemsUpdatedEvent
    ]);
    return showSidecart ? /*#__PURE__*/ _jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/ _jsx(LazyBrandCart, {
            brandToken: brandToken,
            onEvent: trackBrandCartEvents
        })
    }) : null;
};
var ProductLayoutElements = function(param) {
    var productToken = param.productToken;
    var viewport = useViewport();
    var placeholder = /*#__PURE__*/ _jsx(Spacer, {
        height: 508
    });
    var productPageLayoutRequest = IProductPageLayoutRequest.build({
        product_token: productToken,
        grid_size: viewport.isTabletAndBelow ? "MOBILE" : "DESKTOP"
    });
    return /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
        forceRender: isPrerender(),
        customizePlaceholder: placeholder,
        threshold: CAROUSEL_LOADING_OFFSET,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: placeholder,
            children: /*#__PURE__*/ _jsx(LazyProductLayoutElement, {
                request: productPageLayoutRequest,
                productToken: productToken
            })
        })
    });
};
var MoreFromBrand = function(param) {
    var brand = param.brand, product = param.product;
    var shouldShowMoreFromBrand = !isOnlineOnlyRestricted(brand);
    return shouldShowMoreFromBrand ? /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
        forceRender: isPrerender(),
        customizePlaceholder: /*#__PURE__*/ _jsx(BrandInfoRowFallback, {}),
        threshold: CAROUSEL_LOADING_OFFSET,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(BrandInfoRowFallback, {}),
            children: /*#__PURE__*/ _jsx(LazyBrandInfoRow, {
                brandToken: brand.token,
                productToken: product.token,
                brand: brand
            })
        })
    }) : null;
};
var trackBrandCartEvents = function(param) {
    var event = param.event, token = param.token, quantities = param.quantities, message = param.message, brandToken = param.brandToken;
    switch(event){
        case BrandCartEvents.OPEN_PRODUCT_MODAL:
            if (token) {
                trackProductClick(token, "product_page_sidebar_cart_cart_item");
            }
            break;
        case BrandCartEvents.CHANGE_CART_QUANTITY:
            if (quantities && token) {
                var containerName = quantities.newValue === 0 ? "product_page_sidebar_cart" : "product_page_sidebar_cart_dropdown";
                trackEditCartItemEvent(quantities.oldValue, quantities.newValue, containerName, token);
            }
            break;
        case BrandCartEvents.GO_TO_BAG:
            var _RetailerStore_get_retailerToken;
            trackCartProductPageBrandCartViewBagClick((_RetailerStore_get_retailerToken = RetailerStore.get().retailerToken) !== null && _RetailerStore_get_retailerToken !== void 0 ? _RetailerStore_get_retailerToken : "", brandToken !== null && brandToken !== void 0 ? brandToken : "");
            break;
        case BrandCartEvents.CHECKOUT:
            trackPricingProductPageBrandCartCheckoutClick(brandToken !== null && brandToken !== void 0 ? brandToken : "");
            break;
        case BrandCartEvents.CHECKOUT_ERROR:
            trackEvent("/product/cart/checkout-button/shake/".concat(message));
            break;
    }
};
